import { useMemo, useState } from "react";
import { SimpleGrid, Grid, GridItem, VStack, HStack, Text, Skeleton, Box, Badge, Button } from "@chakra-ui/react";
import { PageHeader, SectionCard, StatCard, StatusBadge, CurrencyText, SegmentedProgressBar, EmptyState } from "../../../shared/ui";
import { Check, Clock, Wallet, AlertTriangle } from "../../../shared/icons";
import { formatarDataLonga } from "../../../shared/lib/date";
import { formatBRL } from "../../../shared/lib/currency";
import { STATUS_META, STATUS_COLOR_TOKEN } from "../../plantoes/utils/statusPlantao";
import AlertsList from "../../alertas/components/AlertsList";
import { useAuth } from "../../auth/hooks/useAuth";
import { useDashboardOverviewQuery } from "../hooks/useDashboardOverviewQuery";
import HeroSummaryCard from "../components/HeroSummaryCard";
import { useOnboardingStatusQuery } from "../../onboarding/hooks/useOnboardingStatusQuery";
import { useTrackOnboardingCompleted } from "../../onboarding/hooks/useTrackOnboardingCompleted";
import OnboardingChecklist from "../../onboarding/components/OnboardingChecklist";
import PeriodFilterPopover from "../../recebiveis/components/PeriodFilterPopover";
import ScheduleReadyCard from "../../shiftPatterns/components/ScheduleReadyCard";

// O backend devolve o motivo de um 422 em `erros[0].mensagem` (ex.: "o mês deve estar
// entre 1 e 12") e os demais erros em `mensagem`; sem resposta (rede), cai no genérico.
function mensagemDeErro(error) {
  const dados = error?.response?.data;
  return dados?.erros?.[0]?.mensagem ?? dados?.mensagem ?? "Tente novamente em alguns instantes.";
}

const STAT_ICONS = {
  recebido: { icon: Check, tone: "success" },
  aguardando_nf: { icon: Clock, tone: "warning" },
  previsto: { icon: Wallet, tone: "teal" },
  em_atraso: { icon: AlertTriangle, tone: "danger" },
};

export default function DashboardPage() {
  const { user, perfil } = useAuth();
  // Abre em "todos os períodos", igual a Recebíveis, e soma o mesmo conjunto de plantões
  // (agendados inclusive), então os totais batem com Recebíveis (issue #4). O recorte por
  // mês, disponível no popover, é o mês em que o pagamento cai, como em Recebíveis (issue
  // #18); o período personalizado continua pela data trabalhada.
  const [periodoFiltro, setPeriodoFiltro] = useState(null);
  // Mesmo mapeamento de RecebiveisPage: `null` = todos os períodos, `{ inicio, fim }` =
  // período personalizado (intervalo livre), `{ ano, mes }` = mês inteiro.
  const filtros = useMemo(() => {
    if (!periodoFiltro) return {};
    if (periodoFiltro.inicio && periodoFiltro.fim) {
      return { dataInicio: periodoFiltro.inicio, dataFim: periodoFiltro.fim };
    }
    return { anoPagamento: periodoFiltro.ano, mesPagamento: periodoFiltro.mes };
  }, [periodoFiltro]);
  const { data: overview, isLoading, isError, error, refetch } = useDashboardOverviewQuery(filtros);
  const { data: onboarding, isLoading: isLoadingOnboarding } = useOnboardingStatusQuery();
  useTrackOnboardingCompleted(user?.id, onboarding?.completed);

  const primeiroNome = (perfil?.nome_completo || "").split(" ")[0] || "Doutor(a)";

  // O painel aparece sempre — é ele que mostra quanto o médico tem a receber. Enquanto o
  // onboarding não termina, o checklist fica num card acima dele. Esperar o status evita
  // o checklist "piscar" para quem já concluiu.
  const onboardingResolved = !isLoadingOnboarding && onboarding !== undefined;

  return (
    <>
      <PageHeader
        title={`Olá, ${primeiroNome} 👋`}
        description={formatarDataLonga(new Date())}
        actions={
          onboardingResolved ? (
            <PeriodFilterPopover period={periodoFiltro} onChange={setPeriodoFiltro} />
          ) : null
        }
      />

      {onboardingResolved && !onboarding.completed ? (
        <Box mb={6}>
          <OnboardingChecklist status={onboarding} />
        </Box>
      ) : null}

      {onboardingResolved ? <ScheduleReadyCard /> : null}

      {!onboardingResolved ? (
        <Skeleton height="220px" borderRadius="xl" mb={6} />
      ) : isError ? (
        // Sem isto, qualquer falha (inclusive um filtro recusado com 422) mostrava cards
        // zerados, que se leem como "não houve plantão no período".
        <SectionCard>
          <EmptyState
            icon={AlertTriangle}
            title="Não foi possível carregar a Visão Geral."
            description={mensagemDeErro(error)}
            action={
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            }
          />
        </SectionCard>
      ) : (
        <>
          {isLoading ? (
            <Skeleton height="220px" borderRadius="xl" mb={6} />
          ) : (
            <Box mb={6}>
              <HeroSummaryCard overview={overview} />
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
            {(overview?.stat_cards ?? ["recebido", "aguardando_nf", "previsto", "em_atraso"].map((chave) => ({ chave }))).map(
              (card) => {
                const meta = STAT_ICONS[card.chave] ?? {};
                return (
                  <SectionCard key={card.chave} p={0}>
                    <StatCard
                      label={card.rotulo ?? STATUS_META[card.chave]?.label}
                      value={card.valor ?? 0}
                      icon={meta.icon}
                      tone={meta.tone}
                      isLoading={isLoading}
                      footnote={card.contagem != null ? `${card.contagem} plantão(ões)` : undefined}
                    />
                  </SectionCard>
                );
              }
            )}
          </SimpleGrid>

          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={5} mb={6}>
            <GridItem>
              <SectionCard
                title="Alertas que não podem passar"
                action={
                  overview?.alertas?.length ? (
                    <Badge variant="statusSubtle" colorScheme="danger">
                      {overview.alertas.length}
                    </Badge>
                  ) : null
                }
              >
                <AlertsList alertas={overview?.alertas ?? []} isLoading={isLoading} />
              </SectionCard>
            </GridItem>
            <GridItem>
              <SectionCard title="Composição do período">
                {isLoading ? (
                  <Skeleton height="80px" />
                ) : (
                  <VStack align="stretch" spacing={4}>
                    <SegmentedProgressBar
                      segments={(overview?.composicao_mes ?? []).map((c) => ({
                        label: `${STATUS_META[c.status]?.label ?? c.status} · ${formatBRL(c.valor)}`,
                        value: c.valor,
                        colorToken: STATUS_COLOR_TOKEN[c.status],
                      }))}
                    />
                    <HStack justify="space-between" borderTop="1px solid" borderColor="app-border" pt={3}>
                      <Text fontSize="sm" color="app-muted-fg">
                        Faturamento total do período
                      </Text>
                      <Text fontWeight="bold">
                        <CurrencyText value={overview?.faturamento_previsto ?? 0} />
                      </Text>
                    </HStack>
                  </VStack>
                )}
              </SectionCard>
            </GridItem>
          </Grid>

          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={5}>
            <GridItem>
              <SectionCard title="Plantões recentes">
                {isLoading ? (
                  <Skeleton height="180px" />
                ) : (overview?.plantoes_recentes ?? []).length === 0 ? (
                  <EmptyState title="Nenhum plantão ainda" description="Registre seu primeiro plantão pelo botão no topo." />
                ) : (
                  <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="app-border" />}>
                    {overview.plantoes_recentes.map((p) => (
                      <HStack key={p.id} justify="space-between" py={2.5}>
                        <VStack align="flex-start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {p.vinculo_nome}
                          </Text>
                          <Text fontSize="xs" color="app-muted-fg">
                            {p.data}
                          </Text>
                        </VStack>
                        <HStack spacing={3}>
                          <StatusBadge status={p.status} />
                          <Text fontSize="sm" fontWeight="semibold">
                            <CurrencyText value={p.valor} />
                          </Text>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </SectionCard>
            </GridItem>
            <GridItem>
              <SectionCard title="Quanto cada vínculo paga por hora">
                {isLoading ? (
                  <Skeleton height="180px" />
                ) : (overview?.ranking_vinculos ?? []).length === 0 ? (
                  <EmptyState title="Sem vínculos" description="Cadastre vínculos para comparar o valor por hora." />
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {overview.ranking_vinculos.map((r, i) => {
                      const maxHora = overview.ranking_vinculos[0]?.valor_hora || 1;
                      const pct = Math.max(8, (r.valor_hora / maxHora) * 100);
                      return (
                        <Box key={r.vinculo_id}>
                          <HStack justify="space-between" mb={1}>
                            <HStack spacing={2}>
                              <Text fontSize="sm">{r.nome}</Text>
                              {i === 0 ? (
                                <Badge variant="statusSubtle" colorScheme="success" fontSize="10px">
                                  melhor
                                </Badge>
                              ) : null}
                            </HStack>
                            <Text fontSize="sm" fontWeight="semibold">
                              <CurrencyText value={r.valor_hora} />
                              <Text as="span" fontSize="xs" color="app-muted-fg">
                                /h
                              </Text>
                            </Text>
                          </HStack>
                          <Box bg="app-muted" borderRadius="full" h="6px">
                            <Box bg={i === 0 ? "success.500" : "brand.400"} borderRadius="full" h="100%" w={`${pct}%`} />
                          </Box>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </SectionCard>
            </GridItem>
          </Grid>
        </>
      )}
    </>
  );
}
